import { forwardRef, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Transport.module.css';

const iconProps = { width: 22, height: 22, viewBox: '0 0 24 24', 'aria-hidden': true, focusable: false } as const;

const PlayIcon = () => (
  <svg {...iconProps}>
    <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
  </svg>
);
const StopIcon = () => (
  <svg {...iconProps}>
    <rect x={6.5} y={6.5} width={11} height={11} rx={1.5} fill="currentColor" />
  </svg>
);
const RewindIcon = () => (
  <svg {...iconProps}>
    <rect x={5.5} y={6} width={2.2} height={12} rx={0.8} fill="currentColor" />
    <path d="M18.5 6v12L9.5 12z" fill="currentColor" />
  </svg>
);
const RecordIcon = () => (
  <svg {...iconProps}>
    <circle cx={12} cy={12} r={6.5} fill="currentColor" />
  </svg>
);

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** What the label line says: the playback state, loading or recording progress. */
  status: string;
  /** Status is something happening (recording): shown in red with a dot. */
  live: boolean;
  playing: boolean;
  canPlay: boolean;
  playHint: string;
  onPlay: () => void;
  onStop: () => void;
  /** Back to the start: a button before Play. */
  rewind?: { label: string; disabled?: boolean; onRewind: () => void };
  /** Recording takes (practice): a Record/Finish button after Play. */
  record?: {
    /** A take is in progress (recording or being scored). */
    recording: boolean;
    canRecord: boolean;
    canFinish: boolean;
    hint: string;
    onRecord: () => void;
    onFinish: () => void;
  };
}

/** Play/Stop, and Back to start or Record/Finish, as icon buttons for the controls panel; labels show as hints. */
export const Transport = forwardRef<HTMLDivElement, Props>(function Transport(
  { status, live, playing, canPlay, playHint, onPlay, onStop, rewind, record, ...rest },
  ref,
) {
  const { t } = useTranslation();
  const recording = record?.recording ?? false;
  return (
    <div ref={ref} className={styles.transport} {...rest}>
      <span className={`${styles.label} ${live ? styles.live : ''}`} id="transport-label" role="status">
        {status}
      </span>
      <div className={styles.buttons} role="group" aria-labelledby="transport-label">
        {rewind && (
          <button type="button" className={styles.button} aria-label={rewind.label} data-tip={rewind.label} disabled={rewind.disabled} onClick={rewind.onRewind}>
            <RewindIcon />
          </button>
        )}
        {recording ? (
          <button type="button" className={styles.button} aria-label={t('practice.play')} data-tip={t('practice.play')} disabled>
            <PlayIcon />
          </button>
        ) : playing ? (
          <button type="button" className={styles.button} aria-label={t('practice.stop')} data-tip={t('practice.stop')} onClick={onStop}>
            <StopIcon />
          </button>
        ) : (
          <button
            type="button"
            className={styles.button}
            aria-label={t('practice.play')}
            data-tip={playHint}
            disabled={!canPlay}
            onClick={onPlay}
          >
            <PlayIcon />
          </button>
        )}
        {record && (recording ? (
          <button
            type="button"
            className={`${styles.button} ${styles.record} ${styles.recordingOn}`}
            aria-label={t('take.stop')}
            data-tip={t('take.stop')}
            disabled={!record.canFinish}
            onClick={record.onFinish}
          >
            <StopIcon />
          </button>
        ) : (
          <button
            type="button"
            className={`${styles.button} ${styles.record}`}
            aria-label={t('practice.record')}
            data-tip={record.hint}
            disabled={!record.canRecord}
            onClick={record.onRecord}
          >
            <RecordIcon />
          </button>
        ))}
      </div>
    </div>
  );
});
